import { Button, FormControl, InputLabel, MenuItem, TextField } from "@mui/material";
import PSelect from "components/PSelect";
import { useFormik } from "formik";
import { UpdateUserDocument, UserType } from "generated/graphql";
import { runGqlMutation } from "gqlClient";
import { schemas } from "helpers/FormHelpers";
import { useSnackbar } from "notistack";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectIsOwnerOrSupport, selectMyUserId, setProfileFields } from "redux/slices/session";
import { validate } from "validate.js";
import { fetchUsers, selectUserById } from "redux/slices/client";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { Navigate } from "react-router";

const UserProfile = () => {
  const { userId } = useParamNumbers(["userId"]);
  const user = useAppSelector((state) => selectUserById(state, userId));

  if (userId === undefined || user === undefined) {
    return <Navigate to="/users" replace />;
  }

  const myUserId = useAppSelector(selectMyUserId);
  const dispatch = useAppDispatch();
  const snackpatch = useAppDispatchWithSnackbar();
  const canManageUsers = useAppSelector(selectIsOwnerOrSupport);
  const { enqueueSnackbar } = useSnackbar();
  const formik = useFormik({
    initialValues: {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      userType: user.userType,
    },
    validate: (values) => {
      return validate(values, {
        email: schemas.email,
        firstName: schemas.nonEmpty,
        lastName: schemas.nonEmpty,
      });
    },
    onSubmit: (values) => {
      runGqlMutation(UpdateUserDocument, {
        userId,
        firstName: values.firstName,
        lastName: values.lastName,
        userType: values.userType as UserType,
      })
        .then(() => {
          enqueueSnackbar("User updated", { variant: "success" });
          formik.resetForm({
            values: {
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              userType: values.userType,
            },
          });

          if (userId === myUserId) {
            dispatch(
              setProfileFields({
                firstName: values.firstName,
                lastName: values.lastName,
                userType: values.userType as UserType,
              })
            );
          }
          snackpatch(fetchUsers(), {
            errorMessage: "Failed to load users",
          });
        })
        .catch(() => {
          enqueueSnackbar("Unable to update user", {
            variant: "error",
          });
        });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="flex flex-col max-w-96">
        <TextField id="email" name="email" disabled label="Email" value={formik.values.email} sx={{ mb: 3 }} />
        <TextField
          disabled={!canManageUsers}
          id="firstName"
          name="firstName"
          label="First Name"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          sx={{ mb: 3 }}
        />
        <TextField
          disabled={!canManageUsers}
          id="lastName"
          name="lastName"
          label="Last Name"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          sx={{ mb: 3 }}
        />
        <FormControl size="small" sx={{ mb: 3 }}>
          <InputLabel id="userType-select">Role</InputLabel>
          <PSelect
            disabled={!canManageUsers}
            value={formik.values.userType}
            onChange={formik.handleChange}
            labelId="userType-select"
            label="Role"
            name="userType"
            id="userType">
            <MenuItem value="reviewer">Reviewer</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </PSelect>
        </FormControl>

        {canManageUsers && (
          <Button color="primary" variant="contained" type="submit" disabled={!formik.isValid || !formik.dirty}>
            Save
          </Button>
        )}
      </div>
    </form>
  );
};

export default UserProfile;
